import React, { useRef, useState } from "react";
import axios from "axios";
import { Mic, Loader2 } from "lucide-react";

/* 按住说话 → 上传到 url（公开 nudge 页或带 headers 的内部接口）→ onText(文字) 或 onData(完整返回) */
export function HoldToTalk({ url, zh, disabled, onText, onData, onError, headers, testId = "public-nudge-mic", label, className, iconOnly }) {
  const [state, setState] = useState(null); // null | rec | stt
  const [sec, setSec] = useState(0);
  const mr = useRef(null);
  const chunks = useRef([]);
  const timer = useRef(null);
  const startAt = useRef(0);
  const supported = typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia && typeof window.MediaRecorder !== "undefined";

  const stop = () => { clearInterval(timer.current); if (mr.current && mr.current.state !== "inactive") { try { mr.current.stop(); } catch { /* */ } } };
  const start = async (e) => {
    e.preventDefault();
    if (state || disabled) return;
    if (!supported) { onError?.(zh ? "当前浏览器不支持录音，请直接输入文字" : "Recording not supported — please type"); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const type = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"].find((t) => window.MediaRecorder.isTypeSupported(t)) || "";
      const m = new window.MediaRecorder(stream, type ? { mimeType: type } : undefined);
      chunks.current = [];
      m.ondataavailable = (ev) => { if (ev.data?.size) chunks.current.push(ev.data); };
      m.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks.current, { type: m.mimeType || type || "audio/webm" });
        if (blob.size < 800 || Date.now() - startAt.current < 700) { setState(null); onError?.(zh ? "录音太短，请按住说话" : "Too short — hold to talk"); return; }
        setState("stt");
        try {
          const fd = new FormData();
          fd.append("file", blob, `nudge_${Date.now()}.${(blob.type || "").includes("mp4") ? "m4a" : "webm"}`);
          const { data } = await axios.post(url, fd, { timeout: 90000, headers });
          if (onData) { onData(data); } else {
            const text = (data.text || "").trim();
            if (text) onText?.(text); else onError?.(zh ? "未识别到语音内容，请再试或直接输入" : "Nothing recognised — try again or type");
          }
        } catch (err) { onError?.(err?.response?.data?.detail || (zh ? "语音转写失败，请重试或直接输入文字" : "Transcribe failed — please type")); }
        setState(null);
      };
      m.start(250);
      mr.current = m; startAt.current = Date.now(); setState("rec"); setSec(0);
      timer.current = setInterval(() => setSec((s) => { if (s + 1 >= 60) stop(); return s + 1; }), 1000);
    } catch { onError?.(zh ? "无法访问麦克风，请在浏览器授权后重试" : "Mic access denied"); }
  };
  const release = () => { if (state === "rec") stop(); };

  return (
    <button type="button" data-testid={testId} data-state={state || "idle"} disabled={disabled || state === "stt"}
      onMouseDown={start} onMouseUp={release} onMouseLeave={release} onTouchStart={start} onTouchEnd={release} onTouchCancel={release} onContextMenu={(e) => e.preventDefault()}
      className={className || `select-none touch-none w-full h-12 rounded-full inline-flex items-center justify-center gap-2 text-sm font-semibold transition-colors ${state === "rec" ? "bg-red-500 text-white animate-pulse" : state === "stt" ? "bg-slate-800 text-slate-400" : "bg-slate-800 text-slate-100 border border-slate-700 hover:bg-slate-700"} disabled:opacity-60`}>
      {state === "stt" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mic className="h-4 w-4" />}
      {iconOnly ? (state === "rec" ? <span className="text-[10px]">{sec}s</span> : null) : (state === "rec" ? (zh ? `松开结束 · ${sec}s` : `Release to stop · ${sec}s`) : state === "stt" ? (zh ? "语音识别中…" : "Transcribing…") : (label || (zh ? "按住说话（自动转文字）" : "Hold to talk (auto-transcribe)")))}
    </button>
  );
}
